// The Changelog page's top: Client | Platform, the title, search and the copy
// format. Both tabs draw it, each with its own search.

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cx } from '../../../../components/admin/console';
import type { CopyFormat } from './UpdateCard';

export type Repo = 'client' | 'platform';

const DESCRIPTION: Record<Repo, string> = {
  client: 'Every update sent to Scouts, newest first, with what customers were told and every change that went with it.',
  platform: 'Every change deployed to the platform, newest first, with what it does in plain English and when it went live.',
};

/** What else the box finds, said where it's typed. */
const PLACEHOLDER: Record<Repo, string> = {
  client: 'Search changes, commits, versions',
  platform: 'Search changes, commits, PRs',
};

export function ChangelogHeader({ repo, onRepo, q, onQ, format, onFormat }: {
  repo: Repo;
  onRepo: (repo: Repo) => void;
  q: string;
  onQ: (q: string) => void;
  format: CopyFormat;
  onFormat: (f: CopyFormat) => void;
}) {
  return (
    <div className="space-y-5">
      <div role="group" aria-label="Which changelog" className="-mx-1 flex gap-1 border-b border-slate-800">
        {(['client', 'platform'] as const).map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={repo === r}
            onClick={() => onRepo(r)}
            className={cx(
              '-mb-px min-h-[44px] border-b-2 px-3 text-[15px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500',
              repo === r ? 'border-cyan-400 text-white' : 'border-transparent text-slate-400 hover:text-white',
            )}
          >
            {r === 'client' ? 'Client' : 'Platform'}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="max-w-[460px]">
          <h1 className="text-[28px] font-bold leading-tight tracking-tight text-white">Changelog</h1>
          <p className="mt-2 text-[15px] leading-relaxed text-slate-400">{DESCRIPTION[repo]}</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          <label className="flex h-11 w-full items-center gap-2 rounded-lg border border-slate-700 bg-slate-950/60 px-3 text-slate-500 focus-within:border-cyan-400/60 focus-within:ring-[3px] focus-within:ring-cyan-400/15 sm:w-[260px]">
            <MagnifyingGlassIcon className="h-[18px] w-[18px] flex-none" aria-hidden="true" />
            <input
              type="search"
              value={q}
              onChange={(e) => onQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') onQ(''); }}
              placeholder={PLACEHOLDER[repo]}
              aria-label="Search every change"
              className="min-w-0 flex-1 bg-transparent text-[15px] text-slate-100 placeholder:text-slate-500 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
            />
            {q && (
              <button type="button" onClick={() => onQ('')} aria-label="Clear search" className="-mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-white">
                <XMarkIcon className="h-[18px] w-[18px]" aria-hidden="true" />
              </button>
            )}
          </label>
          <div className="flex flex-1 items-center gap-2.5 sm:flex-none">
            <span className="text-sm text-slate-400">Copy as</span>
            <div role="group" aria-label="Copy format" className="flex flex-1 gap-0.5 rounded-lg border border-slate-700 bg-slate-950/60 p-[3px] sm:flex-none">
              {(['text', 'markdown'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={format === f}
                  onClick={() => onFormat(f)}
                  className={cx(
                    'min-h-[36px] flex-1 rounded-md px-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 sm:flex-none',
                    format === f ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white',
                  )}
                >
                  {f === 'text' ? 'Plain text' : 'Markdown'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The two lists under a tab: what is out, and what is on its way. Counts follow the filter. */
export function TabSwitch({ tab, sent, pending, onPick, labels = ['Sent to Scouts', 'Not sent yet'] }: {
  tab: 'sent' | 'pending';
  sent: number;
  pending: number;
  onPick: (tab: 'sent' | 'pending') => void;
  labels?: [string, string];
}) {
  const item = (id: 'sent' | 'pending', label: string, count: number) => (
    <button
      type="button"
      aria-pressed={tab === id}
      onClick={() => onPick(id)}
      className={cx(
        'inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-md px-3.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 sm:flex-none',
        tab === id ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white',
      )}
    >
      {label}
      <span
        className={cx(
          'min-w-[22px] rounded-full px-1.5 text-[13px] font-semibold tabular-nums',
          id === 'pending' && count > 0 ? 'bg-amber-400/15 text-amber-300' : 'bg-slate-600/50 text-slate-300',
        )}
      >
        {count}
      </span>
    </button>
  );
  return (
    <div role="group" aria-label="Which updates" className="flex w-full gap-0.5 rounded-lg border border-slate-700 bg-slate-950/60 p-[3px] sm:w-fit">
      {item('sent', labels[0], sent)}
      {item('pending', labels[1], pending)}
    </div>
  );
}
